import { InfiniteTable, InfiniteTableColumns, InfiniteTableSortBy, Stack, Text } from '@sixfold/common-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { Routes } from '../../routes';
import { UserGroupOnPlatform } from '../entities';

export interface UserGroupsOnPlatformViewProps {
  company_id: string;
  userGroupsOnPlatform: UserGroupOnPlatform[];
}

interface GroupRow extends UserGroupOnPlatform {
  id: string;
}

const getSortValue = (row: GroupRow, key: keyof GroupRow): string => {
  switch (key) {
    case 'groupId':
      return row.groupId;
    case 'name':
      return row.name;
    case 'platform':
      return row.platform.platform_name;
    default:
      return '';
  }
};

const sortData = (data: GroupRow[], sortBy: InfiniteTableSortBy<keyof GroupRow>): GroupRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp = aVal.localeCompare(bVal);
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const UserGroupsOnPlatformView: React.FC<UserGroupsOnPlatformViewProps> = ({
  company_id,
  userGroupsOnPlatform,
}) => {
  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof GroupRow>>({
    columnId: 'groupId',
    order: 'ASC',
  });

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  const tableData = useMemo(
    () =>
      sortData(
        userGroupsOnPlatform.map((g) => ({ ...g, id: g.groupId })),
        sortBy,
      ),
    [userGroupsOnPlatform, sortBy],
  );

  const columns: InfiniteTableColumns<GroupRow> = useMemo(
    () => [
      {
        id: 'groupId',
        title: 'Group id',
        width: '35%',
        minWidth: '350px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={Routes.CompanyUserGroup.generatePath({ company_id, group_id: row.groupId })}>
            {row.groupId}
          </NavLink>
        ),
      },
      {
        id: 'name',
        title: 'Group name',
        width: '20%',
        minWidth: '130px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.name}</>,
      },
      {
        id: 'platform',
        title: 'Platform',
        width: '15%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.platform.platform_name}</>,
      },
      {
        id: 'company',
        title: 'Company',
        width: '15%',
        minWidth: '100px',
        cellContent: ({ row }) => (
          <NavLink to={Routes.Company.generatePath({ company_id })}>{row.company?.company_name}</NavLink>
        ),
      },
      {
        id: 'users',
        title: 'Users',
        width: '15%',

        minWidth: '60px',
        cellContent: ({ row }) => (
          <NavLink to={Routes.CompanyUserGroup.generatePath({ company_id, group_id: row.groupId })}>Show</NavLink>
        ),
      },
    ],
    [company_id],
  );

  if (tableData.length === 0) {
    return (
      <Stack direction="column" gap="medium">
        <h2 style={{ margin: 0 }}>User Groups</h2>
        <Text>No Groups</Text>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="medium">
      <h2 style={{ margin: 0 }}>User Groups</h2>
      <div className="sub-table-scroll-container" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div ref={containerRefCallback}>
          {scrollParent !== null && (
            <InfiniteTable<GroupRow>
              columns={columns}
              data={tableData}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              scrollParent={scrollParent}
              className="compact-table"
            />
          )}
        </div>
      </div>
    </Stack>
  );
};
